import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { CircleDollarSign, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cancelJobOnRoute } from "../lib/api";

/**
 * "Cancelled on Route" dialog — shared by the driver page and dispatcher
 * job cards. Customer cancelled while the truck was already travelling:
 *   Charge     → job marked completed + flagged chargeable → invoicing list
 *   No Charge  → plain cancelled
 */
export default function CancelOnRouteDialog({ open, job, onOpenChange, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(null); // "charge" | "no_charge" while in flight

  const submit = async (charge) => {
    if (!job?.id || saving) return;
    setSaving(charge ? "charge" : "no_charge");
    try {
      const updated = await cancelJobOnRoute(job.id, charge, reason.trim() || null);
      toast.success(charge
        ? "Marked completed — chargeable cancel-on-route. It's on the invoicing list."
        : "Job cancelled — no charge.");
      setReason("");
      onOpenChange(false);
      onDone?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record cancel-on-route");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setReason(""); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="cancel-on-route-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black tracking-tight">Cancelled on Route</DialogTitle>
          <DialogDescription>
            Customer cancelled <strong>{job?.customer_name || "this job"}</strong> while the truck was on the way. Do we charge?
            <span className="block text-xs mt-1.5 text-slate-500">
              <strong>Charge</strong>: AA / First Rescue jobs bill at the standard rate for the job type. The job is marked completed and goes straight to the invoicing list.
            </span>
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Optional note — e.g. customer got it started themselves"
          rows={2}
          data-testid="cancel-on-route-reason"
        />
        <DialogFooter className="flex flex-row gap-2 sm:gap-2">
          <Button
            variant="outline"
            className="flex-1"
            disabled={!!saving}
            onClick={() => submit(false)}
            data-testid="cancel-on-route-no-charge-btn"
          >
            {saving === "no_charge" ? <Loader2 className="h-4 w-4.5 animate-spin" /> : <Ban className="h-4 w-4.5" />}
            No Charge
          </Button>
          <Button
            className="flex-1 bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            disabled={!!saving}
            onClick={() => submit(true)}
            data-testid="cancel-on-route-charge-btn"
          >
            {saving === "charge" ? <Loader2 className="h-4 w-4.5 animate-spin" /> : <CircleDollarSign className="h-4 w-4.5" />}
            Charge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
